import { useMatches } from "@tanstack/react-router";

import {
  DEFAULT_MODULE,
  findModule,
  findSection,
  firstSection,
  type ModuleDefinition,
  type SectionDefinition,
} from "@/config/modules";

/**
 * Where the URL is, as the navigation map understands it.
 *
 * Read from the matched route's `staticData` rather than parsed from the
 * pathname, so changing a section's URL later is one edit in the map and
 * nothing anywhere else. Falls back to the first module rather than throwing:
 * an unmatched URL is the not-found route's problem, not the shell's.
 */
export function useActiveSection(): {
  module: ModuleDefinition;
  section: SectionDefinition;
} {
  const matches = useMatches();
  const moduleId = matches.findLast((m) => m.staticData.moduleId)?.staticData;

  if (!moduleId?.moduleId) {
    return { module: DEFAULT_MODULE, section: firstSection(DEFAULT_MODULE) };
  }

  const module = findModule(moduleId.moduleId);
  return { module, section: findSection(module, moduleId.sectionId ?? "") };
}

/** Turn a module and section into the URL that renders them. */
export function pathFor(
  module: ModuleDefinition,
  section: SectionDefinition,
): string {
  if (module.path === "/") return `/${section.path.replace(/^\//, "")}`;
  return `${module.path}${section.path}`;
}
